<template>
    <div
        class="pdf-page-selector"
        :class="{ 'fit-width': props.fitWidth }"
        :style="componentStyle"
    >
        <div class="preview-frame">
            <pdf-preview-skeleton v-if="loadingPdf" />
            <div v-else-if="errorMessage" class="preview-state error">{{ errorMessage }}</div>
            <canvas
                ref="previewCanvas"
                class="pdf-canvas"
                :class="{ hidden: loadingPdf || errorMessage }"
                data-testid="pdf-page-preview"
            />
        </div>

        <div class="page-controls">
            <div class="page-navigation">
                <primevue-button
                    text
                    class="plain-btn"
                    :disabled="currentPageIndex <= 0 || loadingPdf"
                    data-testid="pdf-page-prev"
                    @click="goToPreviousPage"
                >
                    <font-awesome-icon :icon="['fas', 'arrow-left']" />
                </primevue-button>
                <pdf-page-indicator
                    :current-page="currentPageNumber"
                    :total-pages="numPages"
                    input-id="pdf-page-current-number"
                    label="Page"
                    total-prefix="of"
                    @page-change="goToInputPage"
                />
                <primevue-button
                    text
                    class="plain-btn"
                    :disabled="currentPageIndex >= numPages - 1 || loadingPdf"
                    data-testid="pdf-page-next"
                    @click="goToNextPage"
                >
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </primevue-button>
            </div>
            <primevue-button
                v-if="selectable"
                text
                type="button"
                class="plain-btn current-page-toggle"
                :class="{ selected: isCurrentPageSelected }"
                :aria-pressed="isCurrentPageSelected"
                data-testid="pdf-page-toggle"
                @click="toggleCurrentPageSelection"
            >
                <slot name="current-page-label" :selected="isCurrentPageSelected">
                    {{ currentPageToggleLabel }}
                </slot>
                <font-awesome-icon :icon="currentPageToggleIcon" />
            </primevue-button>
        </div>

        <template v-if="selectable">
            <div v-if="allowBulkActions" class="selection-controls">
                <div class="bulk-actions">
                    <primevue-button text class="plain-btn" @click="selectAllPages">{{ selectAllLabel }}</primevue-button>
                    <primevue-button text class="plain-btn" @click="clearSelection">{{ clearSelectionLabel }}</primevue-button>
                </div>
            </div>

            <div v-if="normalisedSelectedPageIndexes.length" class="selected-pages">
                <div class="selected-page-chips">
                    <button
                        v-for="pageIndex in normalisedSelectedPageIndexes"
                        :key="pageIndex"
                        type="button"
                        class="selected-page-chip"
                        @click="setCurrentPageIndex(pageIndex)"
                    >
                        Page {{ pageIndex + 1 }}
                        <font-awesome-icon
                            :icon="['fas', 'xmark']"
                            class="remove-page"
                            @click.stop="removePage(pageIndex)"
                        />
                    </button>
                </div>
            </div>
        </template>

        <canvas ref="copyCanvas" class="copy-canvas" aria-hidden="true" />
    </div>
</template>

<script setup lang="ts">
import {
    computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import PrimevueButton from 'primevue/button';
import PdfPageIndicator from './PdfPageIndicator.vue';
import PdfPreviewSkeleton from './PdfPreviewSkeleton.vue';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;

const props = withDefaults(defineProps<{
    pdf: Uint8Array | null;
    modelValue?: Array<number>;
    initialPageIndex?: number;
    selectable?: boolean;
    previewHeight?: string;
    allowBulkActions?: boolean;
    selectAllLabel?: string;
    clearSelectionLabel?: string;
    fitWidth?: boolean;
}>(), {
    modelValue: () => [],
    initialPageIndex: 0,
    selectable: false,
    previewHeight: '28rem',
    allowBulkActions: false,
    selectAllLabel: 'Select all',
    clearSelectionLabel: 'Clear',
    fitWidth: false,
});

const emit = defineEmits<{
    'update:modelValue': [Array<number>];
    'update:currentPageIndex': [number];
    ready: [number];
    rendered: [number];
}>();

const previewCanvas = ref<HTMLCanvasElement | null>(null);
const copyCanvas = ref<HTMLCanvasElement | null>(null);
const currentPageIndex = ref<number>(Math.max(props.initialPageIndex, 0));
const numPages = ref<number>(0);
const loadingPdf = ref<boolean>(false);
const errorMessage = ref<string>('');

let pdfDoc: PDFDocumentProxy | null = null;
let renderRequestId = 0;
let activeRenderTask: { cancel: () => void } | null = null;

const selectable = computed<boolean>(() => props.selectable);
const currentPageNumber = computed<number>(() => currentPageIndex.value + 1);
const componentStyle = computed<Record<string, string>>(() => ({
    '--pdf-page-selector-preview-height': props.previewHeight,
}));
const selectedPageIndexes = ref<Array<number>>([...props.modelValue]);
const normalisedSelectedPageIndexes = computed<Array<number>>(() => normalisePageIndexes(selectedPageIndexes.value));
const isCurrentPageSelected = computed<boolean>(() => normalisedSelectedPageIndexes.value.includes(currentPageIndex.value));
const currentPageToggleLabel = computed<string>(() => (isCurrentPageSelected.value ? 'Remove' : 'Select page'));
const currentPageToggleIcon = computed<[string, string]>(() => ['fas', isCurrentPageSelected.value ? 'xmark' : 'plus']);

watch(() => props.modelValue, (newPageIndexes) => {
    selectedPageIndexes.value = [...newPageIndexes];
}, { deep: true });

function normalisePageIndexes (pageIndexes: Array<number> | undefined): Array<number> {
    const maxExclusive = numPages.value > 0 ? numPages.value : Number.POSITIVE_INFINITY;
    return [...new Set(pageIndexes ?? [])]
        .filter(pageIndex => Number.isInteger(pageIndex) && pageIndex >= 0 && pageIndex < maxExclusive)
        .sort((a, b) => a - b);
}

function emitSelectedPageIndexes (pageIndexes: Array<number>): void {
    const normalisedPageIndexes = normalisePageIndexes(pageIndexes);
    selectedPageIndexes.value = normalisedPageIndexes;
    emit('update:modelValue', normalisedPageIndexes);
}

function setCurrentPageIndex (pageIndex: number): void {
    if (numPages.value <= 0) return;
    const clampedPageIndex = Math.min(Math.max(pageIndex, 0), numPages.value - 1);
    if (currentPageIndex.value === clampedPageIndex) return;
    currentPageIndex.value = clampedPageIndex;
    emit('update:currentPageIndex', clampedPageIndex);
    renderCurrentPage();
}

function goToInputPage (pageNumber: number): void {
    setCurrentPageIndex(pageNumber - 1);
}

function goToPreviousPage (): void {
    setCurrentPageIndex(currentPageIndex.value - 1);
}

function goToNextPage (): void {
    setCurrentPageIndex(currentPageIndex.value + 1);
}

function setCurrentPageSelected (selected: boolean): void {
    const selectedPages = new Set(normalisedSelectedPageIndexes.value);
    if (selected) {
        selectedPages.add(currentPageIndex.value);
    } else {
        selectedPages.delete(currentPageIndex.value);
    }
    emitSelectedPageIndexes([...selectedPages]);
}

function toggleCurrentPageSelection (): void {
    setCurrentPageSelected(!isCurrentPageSelected.value);
}

function removePage (pageIndex: number): void {
    emitSelectedPageIndexes(normalisedSelectedPageIndexes.value.filter(selectedPageIndex => selectedPageIndex !== pageIndex));
}

function selectAllPages (): void {
    if (numPages.value <= 0) return;
    emitSelectedPageIndexes(Array.from({ length: numPages.value }, (_, pageIndex) => pageIndex));
}

function clearSelection (): void {
    emitSelectedPageIndexes([]);
}

async function renderPreview (page: PDFPageProxy): Promise<void> {
    if (!previewCanvas.value) return;
    const container = previewCanvas.value.parentElement;
    if (!container) return;

    const containerWidth = Math.max(container.clientWidth - 10, 280);
    const containerHeight = Math.max(container.clientHeight - 10, 220);
    const unscaledViewport = page.getViewport({ scale: 1 });
    const deviceScale = window.devicePixelRatio || 1;
    const previewScale = (props.fitWidth
        ? containerWidth / unscaledViewport.width
        : Math.min(
            containerWidth / unscaledViewport.width,
            containerHeight / unscaledViewport.height,
        )) * deviceScale;

    const viewportPreview = page.getViewport({ scale: previewScale });
    const ctxPreview = previewCanvas.value.getContext('2d');
    if (!ctxPreview) return;

    previewCanvas.value.width = viewportPreview.width;
    previewCanvas.value.height = viewportPreview.height;
    previewCanvas.value.style.width = `${viewportPreview.width / deviceScale}px`;
    previewCanvas.value.style.height = `${viewportPreview.height / deviceScale}px`;

    activeRenderTask?.cancel();
    const renderTask = page.render({ canvasContext: ctxPreview, viewport: viewportPreview });
    activeRenderTask = renderTask;
    try {
        await renderTask.promise;
    } finally {
        if (activeRenderTask === renderTask) activeRenderTask = null;
    }
}

async function renderCurrentPage (): Promise<void> {
    const requestId = ++renderRequestId;
    await nextTick();
    if (!pdfDoc || !previewCanvas.value || numPages.value <= 0) return;

    try {
        const page = await pdfDoc.getPage(currentPageNumber.value);
        if (requestId !== renderRequestId) return;
        await renderPreview(page);
        emit('rendered', currentPageIndex.value);
    } catch {
        if (requestId !== renderRequestId) return;
        errorMessage.value = 'Unable to render this page.';
    }
}

async function loadPdf (): Promise<void> {
    renderRequestId += 1;
    activeRenderTask?.cancel();
    activeRenderTask = null;
    pdfDoc = null;
    errorMessage.value = '';
    numPages.value = 0;

    if (!props.pdf) return;

    loadingPdf.value = true;
    try {
        const fileCopy = new Uint8Array(props.pdf);
        pdfDoc = await pdfjsLib.getDocument({ data: fileCopy }).promise;
        numPages.value = pdfDoc.numPages;
        currentPageIndex.value = Math.min(Math.max(props.initialPageIndex, 0), Math.max(pdfDoc.numPages - 1, 0));
        emit('update:currentPageIndex', currentPageIndex.value);
        emit('ready', pdfDoc.numPages);
        await renderCurrentPage();
    } catch {
        errorMessage.value = 'Unable to load this PDF.';
    } finally {
        loadingPdf.value = false;
    }
}

async function renderCopyCanvas (page: PDFPageProxy): Promise<void> {
    if (!copyCanvas.value) return;

    const highResScale = 2 * (window.devicePixelRatio || 1);
    const viewportHighRes = page.getViewport({ scale: highResScale });
    const ctxHighRes = copyCanvas.value.getContext('2d');
    if (!ctxHighRes) return;

    copyCanvas.value.width = viewportHighRes.width;
    copyCanvas.value.height = viewportHighRes.height;
    await page.render({ canvasContext: ctxHighRes, viewport: viewportHighRes }).promise;
}

async function copyCurrentPageImage (): Promise<boolean> {
    if (!copyCanvas.value || !pdfDoc) return false;

    try {
        const page = await pdfDoc.getPage(currentPageNumber.value);
        await renderCopyCanvas(page);
        const dataUrl = copyCanvas.value.toDataURL('image/png');
        const res = await fetch(dataUrl);
        const blob = await res.blob();

        await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob }),
        ]);
        return true;
    } catch {
        return false;
    }
}

onMounted(loadPdf);

onBeforeUnmount(() => {
    renderRequestId += 1;
    activeRenderTask?.cancel();
    activeRenderTask = null;
});

watch(() => props.pdf, loadPdf);

watch(() => props.initialPageIndex, (newPageIndex) => {
    setCurrentPageIndex(newPageIndex);
});

defineExpose({
    copyCurrentPageImage,
    currentPageIndex,
    currentPageNumber,
});
</script>

<style scoped lang="scss">
.pdf-page-selector {
    display: flex;
    min-height: 0;
    flex-direction: column;
    gap: var(--defaultSpacing);
    outline: none;
}

.page-controls,
.selection-controls,
.bulk-actions {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
}

.page-controls {
    justify-content: space-between;
}

.pdf-page-selector.fit-width {
    .preview-frame {
        height: auto;
        min-height: 0;
    }

    .pdf-canvas {
        width: 100% !important;
        height: auto !important;
    }
}

.page-navigation {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
}

.preview-frame {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 18rem;
    height: var(--pdf-page-selector-preview-height);
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusInner);
    background: var(--neutral100);
    overflow: hidden;
}

.pdf-canvas {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;

    &.hidden {
        visibility: hidden;
    }
}

.preview-state {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--secondaryFont);

    &.error {
        color: var(--red);
    }
}

.current-page-toggle {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    margin-left: var(--largeSpacing);
}

.selection-controls {
    justify-content: space-between;
    flex-wrap: wrap;
}

.selector-error {
    color: var(--red);
    font-size: var(--smallFontSize);
}

.selected-pages {
    display: grid;
    gap: var(--smallSpacing);
}

.selected-page-chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--smallSpacing);
}

.selected-page-chip {
    display: inline-flex;
    align-items: center;
    gap: var(--extraSmallSpacing);
    padding: var(--extraSmallSpacing) var(--smallSpacing);
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusInner);
    background: var(--neutral100);
    color: var(--primaryFont);
    cursor: pointer;

    &:hover {
        border-color: var(--actionColour);
        color: var(--actionColour);
    }
}

.remove-page {
    font-size: var(--smallFontSize);
}

.copy-canvas {
    display: none;
}
</style>
